/**
 * /verify — Public landing page where customers can paste an order ID
 * and signature to verify the authenticity of a Capexara AI / Cognara invoice.
 *
 * Linked from the "Verify at: …" footer printed on every invoice PDF.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ShieldAlert, Loader2, FileSearch, Hash, Receipt } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyInvoice() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get('order') || '');
  const [sig, setSig] = useState(params.get('sig') || '');
  const [state, setState] = useState({ loading: false, result: null, error: null });

  const run = async (oid, signature) => {
    if (!oid || !signature) return;
    setState({ loading: true, result: null, error: null });
    try {
      const r = await axios.get(`${API}/api/portal/orders/${encodeURIComponent(oid)}/verify`, {
        params: { sig: signature },
      });
      setState({ loading: false, result: r.data, error: null });
    } catch (e) {
      setState({ loading: false, result: null, error: e.response?.data?.detail || 'Verification failed' });
    }
  };

  // Auto-run if both query params are present
  useEffect(() => {
    if (orderId && sig) run(orderId, sig);
    // eslint-disable-next-line
  }, []);

  const onSubmit = (e) => { e.preventDefault(); run(orderId, sig); };
  const result = state.result;
  const ok = !!result?.verified;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: 'radial-gradient(ellipse at top, #0E1320 0%, #06080F 60%)' }}>
      {/* Brand */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #F26B1F 0%, #C25116 100%)' }}>
          <ShieldCheck size={20} className="text-white" />
        </div>
        <div>
          <div className="text-[18px] font-bold text-white tracking-tight" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Capexara <span style={{ color: '#F26B1F' }}>AI</span>
          </div>
          <div className="text-[10.5px] text-white/50 tracking-wider uppercase">Invoice authenticator</div>
        </div>
      </motion.div>

      {/* Card */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="w-full max-w-xl rounded-2xl overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(18px)' }}>

        <div className="p-7 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h1 className="font-semibold text-[26px] text-white leading-tight" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Verify a Capexara invoice
          </h1>
          <p className="text-[13px] text-white/60 mt-1.5">
            Every invoice we issue carries a tamper-evident HMAC stamp. Paste the order ID
            and signature from your PDF footer to confirm authenticity.
          </p>
        </div>

        <form onSubmit={onSubmit} className="p-7 space-y-4" data-testid="verify-form">
          <div>
            <label className="block text-[10.5px] font-bold tracking-[0.16em] uppercase text-white/50 mb-1.5">
              <Receipt size={11} className="inline mr-1.5" /> Order or invoice number
            </label>
            <input value={orderId} onChange={(e) => setOrderId(e.target.value)}
              placeholder="CAPX-2026-00004 or order_abc123…"
              className="w-full h-11 px-4 rounded-lg outline-none text-[13px] font-mono"
              style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
              data-testid="verify-order-id" required />
          </div>
          <div>
            <label className="block text-[10.5px] font-bold tracking-[0.16em] uppercase text-white/50 mb-1.5">
              <Hash size={11} className="inline mr-1.5" /> HMAC signature
            </label>
            <input value={sig} onChange={(e) => setSig(e.target.value)}
              placeholder="64-character hex string from the PDF footer"
              className="w-full h-11 px-4 rounded-lg outline-none text-[13px] font-mono"
              style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' }}
              data-testid="verify-sig" required />
          </div>
          <button type="submit" disabled={state.loading}
            className="w-full h-11 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:translate-y-[-1px]"
            style={{ background: '#F26B1F', color: '#fff' }}
            data-testid="verify-submit">
            {state.loading ? <Loader2 size={14} className="animate-spin" /> : <FileSearch size={14} />}
            {state.loading ? 'Verifying…' : 'Verify invoice'}
          </button>
        </form>

        {/* Result */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="px-7 pb-7" data-testid="verify-result">
            <div className="rounded-xl p-5"
              style={{
                background: ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${ok ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
              }}>
              <div className="flex items-center gap-3">
                {ok
                  ? <ShieldCheck size={28} style={{ color: '#10B981' }} />
                  : <ShieldAlert size={28} style={{ color: '#EF4444' }} />}
                <div>
                  <div className="text-[16px] font-bold" style={{ color: ok ? '#10B981' : '#EF4444' }}>
                    {ok ? 'Verified — invoice is authentic' : 'Could not verify this invoice'}
                  </div>
                  <div className="text-[12px] text-white/60 mt-0.5">
                    {ok
                      ? 'The signature matches what we recorded when the invoice was issued.'
                      : (result.reason === 'invoice_not_found'
                          ? 'No invoice with that ID exists on our books.'
                          : 'The signature does not match. The invoice may have been edited or the values were typed incorrectly.')}
                  </div>
                </div>
              </div>
              {ok && (
                <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-white/40">Invoice #</div>
                    <div className="text-[13px] font-mono font-semibold text-white mt-1">{result.invoice_number}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-white/40">Total</div>
                    <div className="text-[13px] font-mono font-semibold text-white mt-1">
                      {result.currency === 'USD' ? '$' : '₹'}{Number(result.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-white/40">Issued</div>
                    <div className="text-[13px] font-mono font-semibold text-white mt-1">{result.issued || '—'}</div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {state.error && (
          <div className="px-7 pb-7">
            <div className="rounded-lg p-3 text-[12px]"
              style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}>
              {state.error}
            </div>
          </div>
        )}
      </motion.div>

      <p className="mt-8 text-[11px] text-white/40 text-center max-w-md">
        Capexara AI · &quot;Where capital meets cognition.&quot; · Need help? <a href="mailto:hello@capexara.com" className="underline text-white/60">hello@capexara.com</a>
      </p>
    </div>
  );
}
